import { Image } from '@accedo/ctv-image';
import { sleep } from '@accedo/general-utils';
import { logger } from '@accedo/logger';
import { Nav } from '@accedo/spatial-nav';
import { VERTICAL } from '@accedo/virtual-scroll';
import { stylesheet } from 'astroturf';
import { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { sendErrorToTealium, useErrorScreen } from '##/components/Error';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator/LoadingIndicator';
import { nav } from '##/init';
import { config, focusCache } from '##/services/store/store';
import { Config } from '##/services/store/types';
import { RequestRejectionError } from '##/services/tenApi';
import { sendApiRequest } from '##/services/tenApi/apirequest';
import { NewsRegionalApiResponse } from '##/services/tenApi/common/types';
import { MenuItem } from '##/services/tenApi/system/types';
import { useKeyPress } from '##/utils/hooks';
import { PageNavigation } from '##/utils/PageNavigation';
import { handleVideoClick } from '##/utils/TileClickHandlers';
import {
  TealiumErrorCode,
  TealiumErrorContext,
} from '##/services/analytics/Tealium/TealiumErrorTypes';
import { addProgressToEpisodes } from '##/utils/ContinueWatchingUtil';
import { ComponentTypes } from '##/utils/types';
import {
  safeAssignFocus,
  onBackPressOnPagesWithSideNav,
  SIDE_MENU_NAV_ID,
  EXIT_MODAL_NAV_ID,
} from '##/utils';
import { BaseCarousel, CarouselData } from '../BaseCarousel/BaseCarousel';
import { SecondaryNav, SecondaryNavItem } from '../SecondaryNav/SecondaryNav';
import { EpisodeTile } from '../ShowPage/EpisodeTile';
import { StandardCarouselItem } from '../StandardCarousel/types';
import { ShelfTitle } from '../Grid/ShelfTitle';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .news-page-container {
      width: 100vw;
      height: 100vh;
      display:flex;
      font-family: barlow;
  }
  .content-container {
    width: 100vw;
    height: 100vh;
    flex-shrink: 0;
    position: relative;
  }
  .secondary-nav-container {
    flex-shrink: 0;
    height: 100vw;
  }
  .background-image-container {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    transform: translateX(pxToVw(-83));
    z-index: -1;
    .background-image {
      width: 100%;
    }
  }
  .news-details {
    position: absolute;
    bottom: 0;
    max-width: pxToVw(387);
    margin-left: pxToVw(48);
    padding-bottom: pxToVh(80);
    .news-subtitle {
      white-space: pre;
      font-size: pxToVh(19);
    }
  }
  .carousel-header {
    position: absolute;
    margin-left: pxToVw(448);
    margin-top: pxToVw(65);
  }
  .carousel-container {
      width: 58vw;
      margin-left: pxToVw(428);
      padding-left: pxToVw(10);
      margin-top: pxToVw(123);
      overflow-y: hidden;
  }
`;

const secondaryNavId = 'news-secondary-nav';
const newsCarouselNavId = 'newspage-episode-carousel';
const getNavItems = (): SecondaryNavItem[] => {
  const configItems = config.$get() as Config;
  const secondaryNewsItems = configItems?.app?.menuConfig?.primary?.find(
    (menuItem) => menuItem.slug === 'news',
  )?.subItems;

  if (secondaryNewsItems) {
    return secondaryNewsItems.map((item: MenuItem, index: number) => ({
      text: item.label,
      key: item.slug,
      idx: index,
    }));
  }
  return [];
};

const ItemsList: FunctionalComponent<{
  carouselProps: CarouselData | null;
}> = ({ carouselProps }) => {
  if (!carouselProps) {
    return <div className={styles.noContentText}>Loading...</div>;
  }

  if (carouselProps?.items?.length) {
    return <BaseCarousel {...carouselProps} />;
  }

  return <div className={styles.noContentText}>No episodes to show</div>;
};

export const NewsPage: FunctionalComponent = () => {
  const [newsData, setNewsData] = useState<CarouselData | null>(null);
  const [carouselHeader, setCarouselHeader] = useState('');
  const [secondaryData, setSecondaryData] = useState<
    NewsRegionalApiResponse | []
  >([]);
  const [secondaryNavItems, setSecondaryNavItems] = useState<
    SecondaryNavItem[]
  >([]);
  const [showSecondaryNav, setShowSecondaryNav] = useState(true);
  const [currentNavTitle, setCurrentNavTitle] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { showError } = useErrorScreen();

  const fallbackBackgroundImage = '/assets/placeholders/Fallback-Marquee.jpg';
  const configItems = config.$get();
  const newsConfigItem = configItems?.app?.menuConfig?.primary?.find(
    (newsItem) => newsItem.slug === 'news',
  );

  const getNewsData = async (currentTitle: string): Promise<void> => {
    setNewsData(null);
    setCarouselHeader('');

    const newsRegionalApiEndpoint =
      newsConfigItem?.subItems.find((items) => items.slug === currentTitle)
        ?.apiEndpoint || '';

    setIsLoading(true);
    let newsDataResponse = null;

    try {
      newsDataResponse = await sendApiRequest<NewsRegionalApiResponse>(
        newsRegionalApiEndpoint,
      );
      setIsLoading(false);
      setSecondaryData(newsDataResponse);
    } catch (err) {
      sendErrorToTealium({
        errorCode: TealiumErrorCode.FetchNews,
        errorContext: TealiumErrorContext.API,
        errorDetail: JSON.stringify(err),
        errorSource: newsRegionalApiEndpoint,
        errorMessage: '',
      });
      logger.error(err);
      setIsLoading(false);
      showError(
        (err as RequestRejectionError).internalErrorType || 'NETWORK_ERROR',
      );
    }

    if (newsDataResponse) {
      let items = null;
      const title = newsDataResponse[0]?.name
        ? newsDataResponse[0].name
        : newsDataResponse[0].abstractShowDescription;
      const imageUrl = newsDataResponse[0]?.imageUrl;
      const scrollEndBoundary = 2;
      const relevantData = newsDataResponse[0]?.seasons.find(
        (item) => item.menuItems[0].numberOfEpisodes > 0,
      );
      const dataEndpoint = relevantData?.menuItems[0];
      if (dataEndpoint) {
        try {
          const episodes = await sendApiRequest<StandardCarouselItem[]>(
            dataEndpoint.apiEndpoint,
          );
          items = episodes?.length ? episodes : [];
        } catch (err) {
          logger.error(err);
          showError(
            (err as RequestRejectionError).internalErrorType || 'NETWORK_ERROR',
          );
        }
      }
      const carouselProps: CarouselData = {
        items: addProgressToEpisodes(items || []),
        imageUrl,
        Tile: EpisodeTile,
        id: newsCarouselNavId,
        onTileClick: async (episode: StandardCarouselItem) => {
          focusCache.newsPage.$set({
            lastFocusId: episode?.id,
            prevRegion: currentNavTitle,
          });
          handleVideoClick(episode);
        },
        scrollEndBoundary,
        index: 1000,
        orientation: VERTICAL,
        type: ComponentTypes.Standard,
        lazyLoadBoundary: 6,
      };
      setCarouselHeader(title);
      setNewsData(carouselProps);
    }
  };

  const onNavSelect = (key: string): void => {
    setCurrentNavTitle(key);
    const menuItem = getNavItems().find((item) => item.key === key);
    if (menuItem?.text) {
      PageNavigation.subPageRoute$.send(menuItem.text);
    }
  };

  useEffect(() => {
    const navItems = getNavItems();
    setSecondaryNavItems(navItems);
    const { prevRegion } = focusCache.newsPage.$get();
    const cachedGenreIdx = navItems.findIndex(
      (item) => item.key === prevRegion,
    );
    if (navItems.length) {
      const initialEndpoint =
        navItems[cachedGenreIdx > -1 ? cachedGenreIdx : 0].key;
      setCurrentNavTitle(initialEndpoint);
    }
    setLoadingIndicatorVisibility?.(false);
  }, []);

  const onSecondaryItemsUpdate = async (): Promise<void> => {
    const navItems = getNavItems();
    const { prevRegion } = focusCache.newsPage.$get();
    getNewsData(currentNavTitle);

    const cachedRegionIdx = navItems.findIndex(
      (item) => item.key === prevRegion,
    );

    if (cachedRegionIdx > -1) {
      await sleep(1);
      nav.assignFocus(`${secondaryNavId}-item-${cachedRegionIdx}`);
    } else {
      nav.assignFocus(secondaryNavId);
    }
  };

  useEffect(() => {
    if (currentNavTitle === '') {
      return;
    }
    onSecondaryItemsUpdate();
  }, [currentNavTitle]);

  useEffect(() => {
    if (!newsData?.items?.length) {
      return;
    }
    const { lastFocusId } = focusCache.newsPage.$get();
    const prevFocusNewsIdx = newsData?.items.findIndex(
      (item) => item.id === lastFocusId,
    );

    if (prevFocusNewsIdx > -1) {
      nav.assignFocus(`newspage-episode-carousel-item-${prevFocusNewsIdx}`);
    } else {
      nav.assignFocus(secondaryNavId);
    }
    focusCache.newsPage.$clear();
  }, [newsData]);

  useKeyPress('BACK', () => {
    const { parent = '' } = nav?.currentFocusNode || {};

    if (
      parent !== secondaryNavId &&
      parent !== SIDE_MENU_NAV_ID &&
      parent !== EXIT_MODAL_NAV_ID
    ) {
      safeAssignFocus(secondaryNavId);

      return true;
    }

    return onBackPressOnPagesWithSideNav(parent, secondaryNavId);
  });

  return (
    <div className={styles.newsPageContainer}>
      <Nav.Horizontal id="newspage-layout">
        <div className={styles.secondaryNavContainer}>
          <SecondaryNav
            items={secondaryNavItems}
            title={'news'.toUpperCase()}
            navId={secondaryNavId}
            setShowing={setShowSecondaryNav}
            hidden={!showSecondaryNav && !isLoading}
            onSelect={onNavSelect}
            activeKey={currentNavTitle}
          />
        </div>
        {!isLoading && (
          <div className={styles.contentContainer}>
            {secondaryData && (
              <>
                <div className={styles.backgroundImageContainer}>
                  <Image
                    url={secondaryData[0]?.imageUrl || fallbackBackgroundImage}
                    fallbackUrl={fallbackBackgroundImage}
                    className={styles.backgroundImage}
                  />
                </div>{' '}
                <div className={styles.newsDetails}>
                  <div className={styles.newsSubtitle || ''}>
                    {secondaryData[0]?.subtitle.replace('|', '  |  ')}
                  </div>
                </div>
              </>
            )}
            <div className={styles.carouselHeader}>
              <ShelfTitle
                className={styles.shelfTitle}
                title={carouselHeader}
              />
            </div>
            <div className={styles.carouselContainer}>
              <ItemsList carouselProps={newsData} />
            </div>
          </div>
        )}
      </Nav.Horizontal>
    </div>
  );
};
